import React, { useState, useEffect, memo } from "react";
import { Button } from "@/components/ui/button";
import { Monitor, ChevronLeft, ChevronRight, Menu, X, Sun, Clock, Newspaper, Settings, MapPin, Video, RefreshCw } from "lucide-react";
import { supabase } from "../lib/supabase";
import { Slider } from "./ui/slider";
import { Toggle } from "@/components/ui/toggle";
import axios from "axios";
import { isYouTubeUrl, getYouTubeEmbedUrl } from "../utils/youtube";
import { Trash2 as Trash } from "lucide-react";
import { usePromotionsHybrid } from "@/hooks/usePromotionsHybrid";
import PromoDisplayModal from "./PromoDisplayModal";
import { clearPlayerCache } from "../utils/storageUtils";

// Tipo básico para mídia
interface Media {
  id: string;
  title: string;
  description?: string;
  url: string;
  type: string;
  originalType?: string; // Tipo original antes da conversão para blob
  selected_screens: string[];
  display_time?: number;
  active: boolean;
  position_config?: {
    scale: number;
    offsetX: number;
    offsetY: number;
  } | null;
}

interface MediaDisplayProps {
  isAuthenticated: boolean;
  filteredMedias: Media[];
  currentMediaIndex: number;
  onPreviousMedia: () => void;
  onNextMedia: () => void;
  currentUser: string;
  deviceId: string;
  selectedScreen: string;
  screens: any[];
  brightness: number;
  showNewsTicker?: boolean;
  isPaused?: boolean;
  deviceCode?: string;
  isLoadingMedias?: boolean;
  cachedMediasCount?: number;
}

// NOVO COMPONENTE: StorageResetButton

interface StorageResetButtonProps {
  cachedMediasCount?: number;
}

const StorageResetButton = memo(({ cachedMediasCount = 0 }: StorageResetButtonProps) => {
  const [localStorageCount, setLocalStorageCount] = useState(cachedMediasCount);
  const [showModal, setShowModal] = useState(false);
  const [success, setSuccess] = useState(false);

  // Atualizar quando cachedMediasCount mudar (do useMediaCache)
  useEffect(() => {
    setLocalStorageCount(cachedMediasCount);
  }, [cachedMediasCount]);

  // Escutar eventos de reset remoto para zerar o contador instantaneamente
  useEffect(() => {
    const handleRemoteReset = () => {
      console.log('📦 [STORAGE] Remote reset detectado, zerando contador');
      setLocalStorageCount(0);
    };

    const handleCacheUpdate = (event: CustomEvent<{ count: number }>) => {
      console.log('📦 [STORAGE] Cache atualizado:', event.detail.count);
      setLocalStorageCount(event.detail.count);
    };

    window.addEventListener('remoteStorageReset', handleRemoteReset);
    window.addEventListener('cacheCountUpdate', handleCacheUpdate as EventListener);

    return () => {
      window.removeEventListener('remoteStorageReset', handleRemoteReset);
      window.removeEventListener('cacheCountUpdate', handleCacheUpdate as EventListener);
    };
  }, []);

  const handleResetClick = () => {
    setShowModal(true);
    setSuccess(false);
  };

  const handleConfirm = () => {
    clearPlayerCache();
    setLocalStorageCount(0);
    setShowModal(false);
    setSuccess(true);
    setTimeout(() => setSuccess(false), 2000);
  };

  const handleCancel = () => {
    setShowModal(false);
  };

  return (
    <div className="flex flex-col items-center gap-2 mt-4 pt-4 border-t border-gray-200">
      <div className="info flex items-center justify-between w-full">
        <p className="label text-sm font-semibold text-gray-700 mb-0">Resetar storage</p>
        <p className="count text-xs text-gray-500 mb-0">Arquivos locais: <span id="count">{localStorageCount}</span></p>
      </div>
      <Button
        className="w-full bg-red-600 hover:bg-red-700 focus:ring-red-500 rounded-lg flex items-center justify-center transition-all duration-200"
        onClick={handleResetClick}
      >
        <Trash className="w-5 h-5 text-white mr-2" />
        Limpar dados locais
      </Button>

      {/* Modal personalizado */}
      {showModal && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center">
          <div className="bg-white rounded-lg p-6 max-w-xs w-full flex flex-col items-center">
            <Trash className="w-8 h-8 text-red-600 mb-2" />
            <h3 className="text-lg font-semibold mb-2 text-gray-800 text-center">Limpar todos os dados do player?</h3>
            <p className="text-sm text-gray-600 mb-4 text-center">Esta ação irá remover todos os dados salvos localmente neste dispositivo player</p>
            <div className="flex gap-2 w-full">
              <Button className="flex-1" variant="outline" onClick={handleCancel}>Cancelar</Button>
              <Button className="flex-1 bg-red-600 hover:bg-red-700 text-white" onClick={handleConfirm}>Confirmar</Button>
            </div>
          </div>
        </div>
      )}

      {/* Mensagem de sucesso */}
      {success && (
        <div className="fixed bottom-4 left-1/2 transform -translate-x-1/2 z-[9999] bg-green-600 text-white px-4 py-2 rounded shadow-lg text-sm">
          Dados locais limpos com sucesso!
        </div>
      )}
    </div>
  );
});

StorageResetButton.displayName = 'StorageResetButton';

// Função auxiliar para verificar se é URL do Vimeo
const isVimeoUrl = (url: string): boolean => {
  try {
    const urlObj = new URL(url);
    return urlObj.hostname === "vimeo.com" ||
      urlObj.hostname === "www.vimeo.com" ||
      urlObj.hostname === "player.vimeo.com";
  } catch {
    return false;
  }
};

// Função auxiliar para obter URL de embed do Vimeo
const getVimeoEmbedUrl = (url: string): string | null => {
  try {
    const urlObj = new URL(url);
    // Extrair ID do vídeo do Vimeo
    const pathParts = urlObj.pathname.split('/').filter(Boolean);
    const videoId = pathParts.find(part => /^\d+$/.test(part));
    if (videoId) {
      return `https://player.vimeo.com/video/${videoId}?autoplay=1&loop=1&autopause=0&muted=1&background=1`;
    }
    return null;
  } catch {
    return null;
  }
};

// Função para detectar o tipo de mídia baseado na URL
const detectMediaType = (url: string, originalType: string): 'youtube' | 'vimeo' | 'image' | 'video' | 'iframe' => {
  console.log('🔍 [MEDIA DISPLAY] Detectando tipo de mídia:', { url, originalType });

  // IMPORTANTE: Se a URL é blob, precisamos usar outro método de detecção
  // pois blob URLs não têm extensão de arquivo
  const isBlobUrl = url.startsWith('blob:');

  // Se é blob URL, usar o originalType se for válido
  if (isBlobUrl) {
    console.log('🔍 [MEDIA DISPLAY] URL blob detectada, verificando originalType:', originalType);

    // Se originalType indica imagem
    if (originalType === 'image' || originalType === 'link') {
      // Para tipo 'link', assumir imagem se veio do cache (era uma URL de imagem)
      console.log('✅ [MEDIA DISPLAY] Blob detectado como imagem (via originalType)');
      return 'image';
    }
    // Se originalType indica vídeo
    if (originalType === 'video') {
      console.log('✅ [MEDIA DISPLAY] Blob detectado como vídeo (via originalType)');
      return 'video';
    }
    // Fallback: assumir imagem para blob URLs desconhecidos
    console.log('⚠️ [MEDIA DISPLAY] Blob URL sem tipo definido, assumindo imagem');
    return 'image';
  }

  // Verificar YouTube primeiro
  if (isYouTubeUrl(url)) {
    console.log('✅ [MEDIA DISPLAY] Detectado como YouTube');
    return 'youtube';
  }

  // Verificar Vimeo
  if (isVimeoUrl(url)) {
    console.log('✅ [MEDIA DISPLAY] Detectado como Vimeo');
    return 'vimeo';
  }

  // Se o tipo original é 'link', 'url' ou está vazio/nulo, detectar automaticamente
  if (!originalType || originalType === 'link' || originalType === 'url' || originalType === '') {
    const extension = url.toLowerCase().split('.').pop()?.split('?')[0];
    const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp'];
    const videoExtensions = ['mp4', 'webm', 'ogg', 'avi', 'mov', 'wmv', 'flv', 'm4v'];

    console.log('🔍 [MEDIA DISPLAY] Extensão detectada:', extension);

    if (extension && imageExtensions.includes(extension)) {
      console.log('✅ [MEDIA DISPLAY] Detectado como imagem');
      return 'image';
    }
    if (extension && videoExtensions.includes(extension)) {
      console.log('✅ [MEDIA DISPLAY] Detectado como vídeo');
      return 'video';
    }

    // Para URLs sem extensão clara ou sites web, usar iframe
    console.log('✅ [MEDIA DISPLAY] Detectado como iframe (site web)');
    return 'iframe';
  }

  // Manter tipos explícitos válidos
  if (originalType === 'image') {
    console.log('✅ [MEDIA DISPLAY] Usando tipo explícito: imagem');
    return 'image';
  }
  if (originalType === 'video') {
    console.log('✅ [MEDIA DISPLAY] Usando tipo explícito: vídeo');
    return 'video';
  }

  // Para qualquer tipo desconhecido, tentar detectar pela URL ou usar iframe
  console.log('⚠️ [MEDIA DISPLAY] Tipo desconhecido, tentando detectar pela URL');
  const extension = url.toLowerCase().split('.').pop()?.split('?')[0];
  const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp'];
  const videoExtensions = ['mp4', 'webm', 'ogg', 'avi', 'mov', 'wmv', 'flv', 'm4v'];

  if (extension && imageExtensions.includes(extension)) {
    console.log('✅ [MEDIA DISPLAY] Fallback detectado como imagem');
    return 'image';
  }
  if (extension && videoExtensions.includes(extension)) {
    console.log('✅ [MEDIA DISPLAY] Fallback detectado como vídeo');
    return 'video';
  }

  // Fallback final para iframe
  console.log('✅ [MEDIA DISPLAY] Fallback final para iframe');
  return 'iframe';
};

// Componente intermediário para exibir a imagem atual
const CurrentMediaView = memo(({
  media,
  deviceCode,
  brightness,
  filteredMedias,
  currentMediaIndex,
  onPreviousMedia,
  onNextMedia,
  deviceId,
  activatedAccount,
  screens = [],
  selectedScreen = '',
  showNewsTicker,
  setShowNewsTicker,
  showDateTime,
  setShowDateTime,
}: {
  media: Media;
  deviceCode?: string;
  brightness: number;
  filteredMedias: Media[];
  currentMediaIndex: number;
  onPreviousMedia: () => void;
  onNextMedia: () => void;
  deviceId?: string;
  activatedAccount?: string;
  screens?: { id: string, name: string }[];
  selectedScreen?: string;
  showNewsTicker: boolean;
  setShowNewsTicker: (v: boolean) => void;
  showDateTime: boolean;
  setShowDateTime: (v: boolean) => void;
}) => {
  const [localBrightness, setLocalBrightness] = useState(brightness > 1 ? brightness / 100 : brightness);

  useEffect(() => {
    setLocalBrightness(brightness > 1 ? brightness / 100 : brightness);
  }, [brightness]);

  const [connectionStatus, setConnectionStatus] = useState<'online' | 'offline'>('online');
  const [geoEnabled, setGeoEnabled] = useState(false);


  const handleImageLoad = () => {
    console.log('✅ [MEDIA DISPLAY] Imagem carregada com sucesso');
  };

  const handleImageError = (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
    console.error('❌ [MEDIA DISPLAY] Erro ao carregar imagem:', e.currentTarget.src);
    // Verifica se a URL é válida antes de tentar carregar o placeholder
    if (!media?.url || !media.url.startsWith('http')) {
      e.currentTarget.src = "/placeholder.svg";
      return;
    }
    // Tenta carregar a imagem novamente com a URL correta
    if (!e.currentTarget.src.includes('/placeholder.svg')) {
      e.currentTarget.src = "/placeholder.svg";
    }
  };

  const handleVideoError = (e: React.SyntheticEvent<HTMLVideoElement, Event>) => {
    console.error('❌ [MEDIA DISPLAY] Erro ao carregar vídeo:', e.currentTarget.currentSrc);
  };

  const updateDeviceSettings = async (partialSettings: Partial<{ showClock: boolean; showNewsTicker: boolean; geoEnabled?: boolean }>) => {
    if (!deviceCode) {
      console.log('❌ [PLAYER] DeviceCode não disponível para salvar configurações');
      return;
    }

    try {
      const { data, error } = await supabase
        .from('devices')
        .select('settings')
        .eq('code', deviceCode)
        .single();

      if (error) {
        throw error;
      }

      const currentSettings = (data?.settings && typeof data.settings === 'object') ? data.settings : {};
      const newSettings = {
        ...currentSettings,
        ...partialSettings,
        updated_at: new Date().toISOString()
      };

      const { error: updateError } = await supabase
        .from('devices')
        .update({ settings: newSettings })
        .eq('code', deviceCode);

      if (updateError) {
        throw updateError;
      }

      window.dispatchEvent(new CustomEvent('deviceSettingsSync', {
        detail: { deviceCode, settings: newSettings }
      }));

      console.log('✅ [PLAYER] Configurações salvas com sucesso');
    } catch (error) {
      console.error('❌ [PLAYER] Erro ao salvar configurações:', error);
    }
  };

  useEffect(() => {
    const interval = setInterval(() => {
      setConnectionStatus(navigator.onLine ? 'online' : 'offline');
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="relative w-full h-full overflow-hidden bg-black m-0 p-0">
      {(() => {
        const detectedType = detectMediaType(media.url, media.type);
        console.log('🎬 [MEDIA DISPLAY] Renderizando mídia:', {
          title: media.title,
          originalType: media.type,
          detectedType,
          url: media.url
        });

        switch (detectedType) {
          case 'youtube':
            return (
              <div
                style={{
                  position: 'fixed',
                  top: 0,
                  left: 0,
                  width: '100vw',
                  height: '100vh',
                  overflow: 'hidden',
                  background: 'black',
                  zIndex: 1
                }}
              >
                <iframe
                  src={getYouTubeEmbedUrl(media.url) || media.url}
                  style={{
                    filter: `brightness(${Math.max(0.3, localBrightness > 1 ? localBrightness / 100 : localBrightness)})`,
                    // Calcular dimensões para preencher 100% da tela sem margens
                    width: 'max(100vw, 177.78vh)', // 177.78vh = 16:9 ratio em altura
                    height: 'max(100vh, 56.25vw)',  // 56.25vw = 16:9 ratio em largura
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    margin: 0,
                    padding: 0,
                    border: 'none',
                    outline: 'none',
                    minWidth: '100vw',
                    minHeight: '100vh'
                  }}
                  frameBorder="0"
                  loading="eager"
                  scrolling="no"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen={false}
                  title={media.title}
                />
              </div>
            );

          case 'vimeo':
            return (
              <div
                style={{
                  position: 'fixed',
                  top: 0,
                  left: 0,
                  width: '100vw',
                  height: '100vh',
                  overflow: 'hidden',
                  background: 'black',
                  zIndex: 1
                }}
              >
                <iframe
                  src={getVimeoEmbedUrl(media.url) || media.url}
                  style={{
                    filter: `brightness(${Math.max(0.3, localBrightness > 1 ? localBrightness / 100 : localBrightness)})`,
                    width: 'max(100vw, 177.78vh)',
                    height: 'max(100vh, 56.25vw)',
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    margin: 0,
                    padding: 0,
                    border: 'none',
                    outline: 'none',
                    minWidth: '100vw',
                    minHeight: '100vh'
                  }}
                  frameBorder="0"
                  loading="eager"
                  scrolling="no"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen={false}
                  title={media.title}
                />
              </div>
            );

          case 'image':
            console.log('🎬 [MEDIA DISPLAY] Renderizando imagem/GIF:', {
              url: media.url,
              title: media.title,
              isGif: media.url.toLowerCase().includes('.gif')
            });
            return (
              <div className="w-full h-full flex items-center justify-center bg-gray-900 overflow-hidden">
                {media.url && (media.url.startsWith('http') || media.url.startsWith('blob:')) ? (
                  <div className="relative w-full h-full flex items-center justify-center overflow-hidden">
                    <img
                      src={media.url}
                      alt={media.title}
                      className="max-w-none"
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        filter: `brightness(${Math.max(0.3, localBrightness > 1 ? localBrightness / 100 : localBrightness)})`,
                        transform: media.position_config
                          ? `translate(${media.position_config.offsetX}px, ${media.position_config.offsetY}px) scale(${media.position_config.scale})`
                          : undefined,
                        transition: 'transform 0.3s ease-out'
                      }}
                      onLoad={(e) => {
                        console.log('✅ [MEDIA DISPLAY] Imagem/GIF carregada com sucesso:', media.url);
                        handleImageLoad(e);
                      }}
                      onError={(e) => {
                        console.error('❌ [MEDIA DISPLAY] Erro ao carregar imagem/GIF:', media.url, e);
                        handleImageError(e);
                      }}
                    />
                  </div>
                ) : (
                  <div className="text-center text-white">
                    <div className="text-4xl mb-4">🖼️</div>
                    <div className="text-lg mb-2">Imagem não disponível</div>
                    <div className="text-sm text-gray-400">URL inválida ou não fornecida</div>
                  </div>
                )}
              </div>
            );

          case 'video':
            return (
              <div className="w-full h-full flex items-center justify-center bg-gray-900">
                {media.url && (media.url.startsWith('http') || media.url.startsWith('blob:')) ? (
                  <video
                    src={media.url}
                    className="w-full h-full object-cover"
                    style={{
                      filter: `brightness(${Math.max(0.3, localBrightness > 1 ? localBrightness / 100 : localBrightness)})`
                    }}
                    autoPlay
                    muted
                    loop
                    playsInline
                    onLoadStart={handleImageLoad}
                    onError={handleVideoError}
                  />
                ) : (
                  <div className="text-center text-white">
                    <div className="text-4xl mb-4">🎥</div>
                    <div className="text-lg mb-2">Vídeo não disponível</div>
                    <div className="text-sm text-gray-400">URL inválida ou não fornecida</div>
                  </div>
                )}
              </div>
            );

          case 'iframe':
            return (
              <div className="w-full h-full flex items-center justify-center bg-gray-900">
                {media.url && (media.url.startsWith('http') || media.url.startsWith('blob:')) ? (
                  <iframe
                    src={media.url}
                    className="w-full h-full"
                    style={{
                      filter: `brightness(${Math.max(0.3, localBrightness > 1 ? localBrightness / 100 : localBrightness)})`
                    }}
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    title={media.title}
                    sandbox="allow-scripts allow-same-origin allow-forms"
                  />
                ) : (
                  <div className="text-center text-white">
                    <div className="text-4xl mb-4">🌐</div>
                    <div className="text-lg mb-2">Conteúdo web não disponível</div>
                    <div className="text-sm text-gray-400">URL inválida ou não fornecida</div>
                  </div>
                )}
              </div>
            );

          default:
            return (
              <div className="w-full h-full flex items-center justify-center bg-gray-900">
                <div className="text-center text-white">
                  <div className="text-4xl mb-4">❓</div>
                  <div className="text-lg mb-2">Tipo de mídia não reconhecido</div>
                  <div className="text-sm text-gray-400">Tipo: {media.type} | URL: {media.url ? 'Fornecida' : 'Não fornecida'}</div>
                </div>
              </div>
            );
        }
      })()}

      {
        showDateTime && (
          <div className="absolute top-2 left-2 z-30 opacity-60">
            <div className="text-xs text-white bg-black/60 rounded px-2 py-1 shadow">
              {new Date().toLocaleDateString('pt-BR', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' })}
              {' '}
              {new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
            </div>
          </div>
        )
      }

      <div className={`absolute right-4 z-30 ${showNewsTicker ? 'bottom-16' : 'bottom-0'}`}>
        <img
          src="https://locaninja.com/logotipo-watch-white.png"
          alt="Watch Logo"
          className="h-12 w-auto"
          style={{ opacity: 0.33 }}
        />
      </div>

      {
        filteredMedias.length > 1 && (
          <div className="absolute inset-x-4 top-1/2 flex items-center justify-start transform -translate-y-1/2 z-40">
            <Button
              variant="ghost"
              size="sm"
              className="text-white/80 hover:bg-white/20 hover:text-white transition-all duration-200 backdrop-blur-sm bg-black/20 rounded-full p-2"
              onClick={onPreviousMedia}
            >
              <ChevronLeft className="h-6 w-6" />
            </Button>
          </div>
        )
      }
    </div >
  );
});

CurrentMediaView.displayName = 'CurrentMediaView';

class MediaDisplayErrorBoundary extends React.Component<{ children: React.ReactNode }, { hasError: boolean }> {
  constructor(props: any) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  componentDidCatch(error: any, info: any) {
    if (typeof process !== 'undefined' && process?.env?.NODE_ENV !== 'production') {
      console.warn('[MediaDisplay] ErrorBoundary capturou erro:', error, info);
    }
  }
  render() {
    if (this.state.hasError) {
      return (
        <div className="flex flex-col items-center justify-center h-full bg-white text-red-600">
          <h2 className="text-xl font-bold mb-2">Ocorreu um erro inesperado no player</h2>
          <p className="text-base">Tente recarregar a página ou contate o suporte.</p>
        </div>
      );
    }
    return this.props.children;
  }
}

function MediaDisplay({
  isAuthenticated,
  filteredMedias,
  currentMediaIndex,
  onPreviousMedia,
  onNextMedia,
  currentUser,
  deviceId,
  selectedScreen,
  screens,
  brightness,
  showNewsTicker: showNewsTickerProp = true,
  isPaused = false,
  deviceCode,
  isLoadingMedias = false,
  cachedMediasCount = 0
}: MediaDisplayProps) {
  const [showNewsTicker, setShowNewsTicker] = useState(showNewsTickerProp);

  // Estados para preload de vídeos do YouTube
  const [preloadedYouTubeVideos, setPreloadedYouTubeVideos] = useState<Map<string, boolean>>(new Map());
  const [youtubePreloadRefs, setYoutubePreloadRefs] = useState<Map<string, HTMLIFrameElement>>(new Map());

  // Estado para barra progressiva e cronômetro
  const currentMedia = filteredMedias && filteredMedias.length > 0 ? filteredMedias[currentMediaIndex] : null;
  const displayTime = currentMedia?.display_time || 5000;
  const [progress, setProgress] = useState(0);
  const [timeLeft, setTimeLeft] = useState(Math.floor(displayTime / 1000));
  const progressRef = React.useRef<NodeJS.Timeout | null>(null);

  // Atualiza barra e cronômetro a cada segundo
  useEffect(() => {
    if (!currentMedia) return;
    setProgress(0);
    setTimeLeft(Math.floor(displayTime / 1000));
    if (progressRef.current) clearInterval(progressRef.current);
    const start = Date.now();
    progressRef.current = setInterval(() => {
      const elapsed = Date.now() - start;
      const pct = Math.min(1, elapsed / displayTime);
      setProgress(pct);
      setTimeLeft(Math.max(0, Math.ceil((displayTime - elapsed) / 1000)));
      if (pct >= 1) {
        if (progressRef.current) clearInterval(progressRef.current);
      }
    }, 200);
    return () => {
      if (progressRef.current) clearInterval(progressRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentMediaIndex, currentMedia?.id, displayTime]);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  // Estado para indicar se a mídia atual está no cache
  const [isMediaCached, setIsMediaCached] = useState(false);
  const [cacheCheckPending, setCacheCheckPending] = useState(false);

  // Verificar se a mídia atual está no cache do Service Worker
  useEffect(() => {
    const checkCache = async () => {
      if (!currentMedia?.url || !('caches' in window)) {
        setIsMediaCached(false);
        return;
      }

      setCacheCheckPending(true);
      try {
        const cache = await caches.open('watch-media-cache-v2');
        const response = await cache.match(currentMedia.url);
        setIsMediaCached(!!response);
        console.log(`📦 [CACHE CHECK] Mídia "${currentMedia.title}": ${response ? '✅ Em cache' : '⬇️ Do servidor'}`);
      } catch (error) {
        setIsMediaCached(false);
      } finally {
        setCacheCheckPending(false);
      }
    };

    checkCache();
  }, [currentMedia?.url, currentMedia?.title]);

  // Limpeza automática de mídias órfãs do cache
  // Remove do cache mídias que não estão mais na playlist
  useEffect(() => {
    const cleanupOrphanedCache = async () => {
      if (!filteredMedias || filteredMedias.length === 0 || !('caches' in window)) {
        return;
      }

      try {
        const cache = await caches.open('watch-media-cache-v2');
        const cachedRequests = await cache.keys();

        // Criar um Set com todas as URLs da playlist atual
        const playlistUrls = new Set(filteredMedias.map(m => m.url).filter(Boolean));

        let removedCount = 0;

        for (const request of cachedRequests) {
          const cachedUrl = request.url;

          // Se a URL cacheada não está na playlist atual, é órfã - remover
          if (!playlistUrls.has(cachedUrl)) {
            await cache.delete(request);
            removedCount++;
            console.log(`🧹 [CACHE CLEANUP] Removida mídia órfã: ${cachedUrl.substring(0, 60)}...`);
          }
        }

        if (removedCount > 0) {
          console.log(`🧹 [CACHE CLEANUP] Total de ${removedCount} mídia(s) órfã(s) removida(s) do cache`);
        }
      } catch (error) {
        console.error('❌ [CACHE CLEANUP] Erro ao limpar cache:', error);
      }
    };

    // Executar limpeza com delay para não impactar carregamento inicial
    const cleanupTimer = setTimeout(cleanupOrphanedCache, 5000);

    return () => clearTimeout(cleanupTimer);
  }, [filteredMedias]);

  const [isStreamModalOpen, setIsStreamModalOpen] = useState(false);
  const [streamUrl, setStreamUrl] = useState<string>("");

  const [showDateTime, setShowDateTime] = useState(true);
  const [geoEnabled, setGeoEnabled] = useState(false);

  // Effect para preload de vídeos do YouTube
  useEffect(() => {
    if (!filteredMedias || filteredMedias.length === 0) return;

    // Identifica vídeos do YouTube que precisam ser precarregados
    const youtubeVideos = filteredMedias.filter(media =>
      media.url && isYouTubeUrl(media.url)
    );

    youtubeVideos.forEach((media, index) => {
      const mediaId = media.id;

      // Se já está precarregado, pula
      if (preloadedYouTubeVideos.has(mediaId)) return;

      // Cria iframe invisível para preload
      const iframe = document.createElement('iframe');
      const embedUrl = getYouTubeEmbedUrl(media.url);

      if (embedUrl) {
        iframe.src = embedUrl;
        iframe.style.position = 'fixed';
        iframe.style.top = '-1000px';
        iframe.style.left = '-1000px';
        iframe.style.width = '1px';
        iframe.style.height = '1px';
        iframe.style.opacity = '0';
        iframe.style.pointerEvents = 'none';
        iframe.style.visibility = 'hidden';
        iframe.frameBorder = '0';
        iframe.loading = 'eager';

        // Quando carregado, marca como precarregado
        iframe.onload = () => {
          console.log(`✅ [PRELOAD] Vídeo YouTube pré-carregado: ${media.title}`);
          setPreloadedYouTubeVideos(prev => new Map(prev.set(mediaId, true)));
        };

        iframe.onerror = () => {
          console.error(`❌ [PRELOAD] Erro ao pré-carregar: ${media.title}`);
        };

        // Adiciona ao DOM
        document.body.appendChild(iframe);

        // Armazena referência
        setYoutubePreloadRefs(prev => new Map(prev.set(mediaId, iframe)));
      }
    });

    // Cleanup: remove iframes de vídeos que não estão mais na lista
    return () => {
      youtubePreloadRefs.forEach((iframe, mediaId) => {
        if (!filteredMedias.find(media => media.id === mediaId)) {
          if (iframe.parentNode) {
            iframe.parentNode.removeChild(iframe);
          }
        }
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredMedias]);

  // Cleanup na desmontagem do componente
  useEffect(() => {
    return () => {
      youtubePreloadRefs.forEach(iframe => {
        if (iframe.parentNode) {
          iframe.parentNode.removeChild(iframe);
        }
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [localBrightness, setLocalBrightness] = useState(brightness > 1 ? brightness / 100 : brightness);
  const [connectionStatus, setConnectionStatus] = useState<'online' | 'offline'>('online');
  const [batteryLevel, setBatteryLevel] = useState<number>(85);
  const [showGeoModal, setShowGeoModal] = useState(false);

  // Hook híbrido para gerenciar promoções com detecção garantida - só executar quando usuário estiver ativado
  const { activePromo, showPromoModal, closePromoModal, isLoading: isLoadingPromotions } = usePromotionsHybrid(
    currentUser ? selectedScreen : undefined,
    currentUser || undefined,
    filteredMedias,
    currentMediaIndex
  );

  const handleDeviceSettingsUpdate = async (settings: { showClock?: boolean; showNewsTicker?: boolean; geoEnabled?: boolean; brightness?: number }) => {
    if (!deviceCode) {
      console.log('❌ [PLAYER] DeviceCode não disponível para salvar configurações');
      return;
    }

    try {
      const { data, error } = await supabase
        .from('devices')
        .select('settings')
        .eq('code', deviceCode)
        .single();

      if (error) throw error;

      const currentSettings = (data?.settings && typeof data.settings === 'object') ? data.settings : {};
      const newSettings = {
        ...currentSettings,
        ...settings,
        updated_at: new Date().toISOString()
      };

      const { error: updateError } = await supabase
        .from('devices')
        .update({ settings: newSettings })
        .eq('code', deviceCode);

      if (updateError) throw updateError;

      if ('showClock' in settings) {
        setShowDateTime(settings.showClock!);
      }
      if ('showNewsTicker' in settings) {
        setShowNewsTicker(settings.showNewsTicker!);
      }
      if ('geoEnabled' in settings) {
        setGeoEnabled(settings.geoEnabled!);
      }
      if ('brightness' in settings) {
        setLocalBrightness(settings.brightness! > 1 ? settings.brightness! / 100 : settings.brightness!);
      }

      window.dispatchEvent(new CustomEvent('deviceSettingsSync', {
        detail: { deviceCode, settings: newSettings }
      }));

      console.log('✅ [PLAYER] Configurações salvas com sucesso');
    } catch (error) {
      console.error('❌ [PLAYER] Erro ao salvar configurações:', error);

      if ('showClock' in settings) {
        setShowDateTime(!settings.showClock!);
      }
      if ('showNewsTicker' in settings) {
        setShowNewsTicker(!settings.showNewsTicker!);
      }
      if ('geoEnabled' in settings) {
        setGeoEnabled(!settings.geoEnabled!);
      }
      if ('brightness' in settings) {
        setLocalBrightness(localBrightness);
      }
    }
  };

  useEffect(() => {
    const handleSync = (event: any) => {
      if (!event.detail) return;
      const { deviceCode: syncCode, settings } = event.detail;
      if (deviceCode && syncCode === deviceCode && settings) {
        if ('showClock' in settings) {
          setShowDateTime(settings.showClock);
        }
        if ('showNewsTicker' in settings) {
          setShowNewsTicker(settings.showNewsTicker);
        }

        console.log('🔄 [PLAYER] Configurações sincronizadas via evento global');
      }
    };

    window.addEventListener('deviceSettingsSync', handleSync);

    const fetchInitialState = async () => {
      if (!deviceCode) return;

      try {
        const { data, error } = await supabase
          .from('devices')
          .select('settings')
          .eq('code', deviceCode)
          .single();

        if (error) throw error;

        if (data?.settings && typeof data.settings === 'object') {
          const settings = data.settings as { showClock?: boolean; showNewsTicker?: boolean };
          setShowDateTime(settings.showClock !== false);
          setShowNewsTicker(settings.showNewsTicker !== false);
        }
      } catch (error) {
        console.error('❌ [PLAYER] Erro ao carregar configurações iniciais:', error);
      }
    };

    fetchInitialState();

    return () => window.removeEventListener('deviceSettingsSync', handleSync);
  }, [deviceCode]);

  useEffect(() => {
    const checkGeoLocation = async () => {
      if (!deviceCode) return;

      try {
        const { data, error } = await supabase
          .from('devices')
          .select('settings')
          .eq('code', deviceCode)
          .single();

        if (error) throw error;

        const settings = data?.settings as { geoEnabled?: boolean };
        const geoStatus = settings?.geoEnabled || false;
        setGeoEnabled(geoStatus);

        if (!geoStatus) {
          setShowGeoModal(true);
          setIsMenuOpen(true);
        }
      } catch (error) {
        console.error('❌ [PLAYER] Erro ao verificar geolocalização:', error);
      }
    };

    const timer = setTimeout(checkGeoLocation, 1000);
    return () => clearTimeout(timer);
  }, [deviceCode]);

  const [cotacoes, setCotacoes] = useState<{ btc?: number; bnb?: number; usd?: number }>({});
  const [noticias, setNoticias] = useState<string[]>(['Carregando informações...']);
  const [noticiaIndex, setNoticiaIndex] = useState(0);
  const [noticiasErro, setNoticiasErro] = useState(false);

  useEffect(() => {
    const fetchAllData = async () => {
      try {
        // Buscar notícias brasileiras via RSS2JSON (gratuito e estável)
        const newsResponse = await axios.get(
          'https://api.rss2json.com/v1/api.json?rss_url=https://g1.globo.com/rss/g1/'
        ).catch(() =>
          axios.get('https://api.rss2json.com/v1/api.json?rss_url=https://agenciabrasil.ebc.com.br/rss/ultimasnoticias/feed.xml')
        ).catch(() => null);

        // Buscar cotações de criptomoedas (CoinGecko - gratuito)
        const cryptoResponse = await axios.get(
          "https://api.coingecko.com/api/v3/simple/price?ids=bitcoin&vs_currencies=brl&include_24hr_change=true"
        );

        // Buscar cotações de moedas (AwesomeAPI - gratuito)
        const currencyResponse = await axios.get("https://economia.awesomeapi.com.br/json/last/USD-BRL,EUR-BRL");

        // Processar notícias
        const newsItems = [];
        if (newsResponse?.data?.items) {
          newsItems.push(...newsResponse.data.items.slice(0, 8).map((item: any) =>
            `📰 ${item.title.replace(/<[^>]*>/g, '').trim()}`
          ));
        }

        // Processar dados de crypto
        const cryptoItems = [];
        if (cryptoResponse.data?.bitcoin) {
          const btc = cryptoResponse.data.bitcoin;
          const change = btc.brl_24h_change || 0;
          const changeIcon = change >= 0 ? '📈' : '📉';
          cryptoItems.push(`₿ Bitcoin: R$ ${btc.brl.toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} ${changeIcon} ${change.toFixed(2)}%`);
        }

        // Processar dados de moedas
        const currencyItems = [];
        if (currencyResponse.data) {
          const data = currencyResponse.data;
          if (data.USDBRL) {
            const usdChange = parseFloat(data.USDBRL.pctChange);
            const usdIcon = usdChange >= 0 ? '📈' : '📉';
            currencyItems.push(`💵 Dólar: R$ ${parseFloat(data.USDBRL.bid).toFixed(2)} ${usdIcon} ${usdChange.toFixed(2)}%`);
          }
          if (data.EURBRL) {
            const eurChange = parseFloat(data.EURBRL.pctChange);
            const eurIcon = eurChange >= 0 ? '📈' : '📉';
            currencyItems.push(`💶 Euro: R$ ${parseFloat(data.EURBRL.bid).toFixed(2)} ${eurIcon} ${eurChange.toFixed(2)}%`);
          }
        }

        // Combinar todos os dados intercalados
        const allData = [];
        const maxLength = Math.max(newsItems.length, cryptoItems.length, currencyItems.length);

        for (let i = 0; i < maxLength; i++) {
          if (newsItems[i]) allData.push(newsItems[i]);
          if (cryptoItems[i]) allData.push(cryptoItems[i]);
          if (currencyItems[i]) allData.push(currencyItems[i]);
        }

        // Adicionar timestamp
        allData.push(`🕐 Atualizado em ${new Date().toLocaleString('pt-BR', { hour: '2-digit', minute: '2-digit' })}`);

        setNoticias(allData.length > 0 ? allData : ['📰 Aguardando dados...']);

        // Manter compatibilidade com código existente
        setCotacoes({
          btc: cryptoResponse.data.bitcoin?.brl,
          usd: currencyResponse.data.USDBRL ? parseFloat(currencyResponse.data.USDBRL.bid) : undefined,
        });

      } catch (err) {
        console.warn("[TICKER] Erro ao buscar dados:", err);

        // Dados de fallback
        const fallbackData = [
          "📰 Sistema de notícias temporariamente indisponível",
          "💰 Cotações serão atualizadas em breve",
          `🕐 ${new Date().toLocaleString('pt-BR', { hour: '2-digit', minute: '2-digit' })} - Tentando reconectar...`
        ];
        setNoticias(fallbackData);
      }
    };

    fetchAllData();
    const interval = setInterval(fetchAllData, 5 * 60 * 1000); // Atualizar a cada 5 minutos
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (noticias.length === 0) return;
    const trocarNoticia = () => {
      setNoticiaIndex((prev) => (prev + 1) % noticias.length);
    };
    const timeout = setTimeout(trocarNoticia, 15000); // Trocar a cada 15 segundos
    return () => clearTimeout(timeout);
  }, [noticias, noticiaIndex]);

  useEffect(() => {
    const interval = setInterval(() => {
      setConnectionStatus(navigator.onLine ? 'online' : 'offline');
      setBatteryLevel(prev => Math.max(10, prev - Math.random() * 2));
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  return (
    <MediaDisplayErrorBoundary>
      <div className="absolute inset-0 flex items-center justify-center" style={{ display: isPaused ? "none" : "flex" }}>
        <button
          className="absolute top-4 right-4 z-50 bg-black/60 hover:bg-black/80 text-white rounded-full p-2 shadow-lg focus:outline-none"
          onClick={() => setIsMenuOpen(true)}
          aria-label="Abrir menu"
        >
          <Menu className="h-7 w-7" />
        </button>

        {isMenuOpen && (
          <div className="fixed inset-0 z-[100] flex justify-end">
            <div
              className="fixed inset-0 bg-black/40 transition-opacity duration-300 z-[100]"
              onClick={() => setIsMenuOpen(false)}
            />
            <div className="relative w-[90vw] max-w-[400px] h-full bg-white shadow-xl z-[101] animate-slide-in-right flex flex-col">
              {/* Header fixo */}
              <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 flex-shrink-0">
                <h2 className="text-base sm:text-lg font-semibold flex items-center gap-2">
                  <Settings className="h-4 w-4 sm:h-5 sm:w-5" /> Configurações
                </h2>
                <button
                  className="text-gray-400 hover:text-gray-700 p-1"
                  onClick={() => setIsMenuOpen(false)}
                  aria-label="Fechar menu"
                >
                  <X className="h-5 w-5 sm:h-6 sm:w-6" />
                </button>
              </div>

              {/* Conteúdo principal - flexível */}
              <div className="flex-1 flex flex-col justify-between p-3 sm:p-4 min-h-0">
                {/* Seção superior com informações */}
                <div className="space-y-3">
                  {/* Dispositivo */}
                  <div className="p-2 sm:p-3 bg-gray-100 rounded-lg">
                    <div className="text-[10px] sm:text-xs text-gray-500 mb-1 flex items-center gap-1">
                      <Monitor className="h-3 w-3 sm:h-4 sm:w-4" /> Dispositivo
                    </div>
                    <div className="font-mono text-xs sm:text-sm text-gray-800">ID: {deviceId || '-'}</div>
                    <div className="font-mono text-xs sm:text-sm text-gray-800">Código: {deviceCode || '-'}</div>
                    <div className="font-mono text-[10px] sm:text-xs text-gray-500">Usuário: {currentUser || '-'}</div>
                  </div>

                  {/* Mídia atual */}
                  <div>
                    <div className="text-[10px] sm:text-xs text-gray-500 mb-1 flex items-center gap-1">
                      <Video className="h-3 w-3 sm:h-4 sm:w-4" />
                      <span>Mídia: {currentMediaIndex + 1} / {filteredMedias?.length || 0}</span>
                    </div>
                    <div className="flex items-center justify-between mb-1.5 px-0.5">
                      <div className="flex items-center gap-2 sm:gap-3">
                        <div className="flex items-center gap-1">
                          <span className={`w-2 h-2 rounded-full ${!isMediaCached ? 'bg-yellow-500' : 'bg-gray-300'}`} />
                          <span className={`text-[10px] sm:text-xs ${!isMediaCached ? 'text-yellow-700 font-medium' : 'text-gray-400'}`}>
                            Servidor
                          </span>
                        </div>
                        <div className="flex items-center gap-1">
                          <span className={`w-2 h-2 rounded-full ${isMediaCached ? 'bg-green-500' : 'bg-gray-300'}`} />
                          <span className={`text-[10px] sm:text-xs ${isMediaCached ? 'text-green-700 font-medium' : 'text-gray-400'}`}>
                            Storage
                          </span>
                        </div>
                      </div>
                      <button
                        onClick={() => window.location.reload()}
                        className="flex items-center gap-1 text-[10px] sm:text-xs text-green-600 hover:text-green-700 font-medium"
                      >
                        Atualizar <RefreshCw className="h-3 w-3 sm:h-4 sm:w-4" />
                      </button>
                    </div>
                    <div className="p-2 bg-gray-50 rounded text-xs sm:text-sm text-gray-800 truncate">
                      {filteredMedias?.[currentMediaIndex]?.title || 'Nenhuma mídia'}
                      {filteredMedias?.[currentMediaIndex] && (
                        <span className="text-[10px] sm:text-xs text-gray-500 ml-1">
                          [{filteredMedias[currentMediaIndex].type}]
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Brilho */}
                  <div>
                    <label className="flex items-center gap-1 text-xs sm:text-sm font-medium text-gray-700 mb-1">
                      <Sun className="h-3 w-3 sm:h-4 sm:w-4" /> Brilho: {Math.round(localBrightness * 100)}%
                    </label>
                    <Slider
                      min={30}
                      max={100}
                      step={1}
                      value={[Math.round(localBrightness * 100)]}
                      onValueChange={async ([v]) => {
                        const brightnessValue = v / 100;
                        setLocalBrightness(brightnessValue);
                        try {
                          if (deviceCode) {
                            await supabase
                              .from('devices')
                              .update({ settings: { brightness: v, updated_at: new Date().toISOString() } })
                              .eq('code', deviceCode);
                            window.dispatchEvent(new CustomEvent('deviceSettingsSync', {
                              detail: { deviceCode, settings: { brightness: v } }
                            }));
                          }
                        } catch (error) {
                          console.error('❌ [PLAYER] Erro ao salvar brilho:', error);
                        }
                      }}
                    />
                  </div>

                  {/* Toggles em grid responsivo */}
                  <div className="grid grid-cols-1 gap-2">
                    <div className="flex items-center justify-between py-1.5 px-2 bg-gray-50 rounded-lg">
                      <span className="flex items-center gap-1.5 text-xs sm:text-sm">
                        <Clock className="h-3 w-3 sm:h-4 sm:w-4" /> Data/Hora
                      </span>
                      <Toggle
                        checked={showDateTime}
                        onChange={async (checked) => {
                          setShowDateTime(checked);
                          await handleDeviceSettingsUpdate({ showClock: checked });
                        }}
                        size="sm"
                        aria-label="Exibir Data/Hora"
                      />
                    </div>
                    <div className="flex items-center justify-between py-1.5 px-2 bg-gray-50 rounded-lg">
                      <span className="flex items-center gap-1.5 text-xs sm:text-sm">
                        <Newspaper className="h-3 w-3 sm:h-4 sm:w-4" /> Tarja de Notícias
                      </span>
                      <Toggle
                        checked={showNewsTicker}
                        onChange={async (checked) => {
                          setShowNewsTicker(checked);
                          await handleDeviceSettingsUpdate({ showNewsTicker: checked });
                        }}
                        size="sm"
                        aria-label="Exibir Tarja de Notícias"
                      />
                    </div>
                    <div className="flex items-center justify-between py-1.5 px-2 bg-gray-50 rounded-lg">
                      <span className="flex items-center gap-1.5 text-xs sm:text-sm">
                        <MapPin className="h-3 w-3 sm:h-4 sm:w-4" /> Geolocalização
                      </span>
                      <Toggle
                        checked={geoEnabled}
                        onChange={async (checked) => {
                          setGeoEnabled(checked);
                          await handleDeviceSettingsUpdate({ geoEnabled: checked });
                          if (!checked) window.location.reload();
                        }}
                        size="sm"
                        aria-label="Geolocalização"
                      />
                    </div>
                  </div>

                  {/* Botão de reset */}
                  <StorageResetButton cachedMediasCount={cachedMediasCount} />
                </div>

                {/* Rodapé compacto */}
                <div className="pt-3 border-t border-gray-200 text-center flex-shrink-0">
                  <img
                    src="https://locaninja.com/logotipo-watch.png"
                    alt="Watch Logo"
                    className="h-6 sm:h-8 mx-auto mb-1 opacity-80"
                  />
                  <p className="text-[9px] sm:text-xs text-gray-400">
                    © 2024 Watch Technologies Inc.
                  </p>
                  <p className="text-[9px] sm:text-xs text-gray-400">
                    Versão V1.01
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {showGeoModal && (
          <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/50">
            <div className="bg-white rounded-lg p-6 mx-4 max-w-sm shadow-xl">
              <h3 className="text-lg font-semibold mb-3 flex items-center justify-center gap-2">
                <MapPin className="h-5 w-5 text-green-600" />
                Geolocalização Desativada
              </h3>
              <p className="text-sm text-gray-600 mb-4 text-center">
                Ative a geolocalização deste aparelho para o funcionamento contínuo.
              </p>
              <Button
                className="w-full"
                onClick={async () => {
                  setGeoEnabled(true);
                  await handleDeviceSettingsUpdate({ geoEnabled: true });
                  setShowGeoModal(false);
                }}
              >
                Ativar
              </Button>
            </div>
          </div>
        )}


        {/* Barra progressiva fina e cronômetro regressivo */}
        {showNewsTicker && (
          <div className="fixed z-[51] flex items-end pointer-events-none" style={{ bottom: 50, left: 10 }}>
            <div className="relative w-[200px] flex items-center">
              <div className="flex-1 h-1 bg-white/20 rounded-full overflow-hidden">
                <div
                  className="h-1 bg-green-400 transition-all duration-200"
                  style={{ width: `${progress * 100}%` }}
                />
              </div>
              <span className="ml-0.5 text-xs text-white/70 font-mono min-w-[32px] text-right select-none" style={{ textShadow: '0 1px 2px #0008', marginLeft: '3px' }}>
                {timeLeft}s
              </span>
            </div>
          </div>
        )}

        <CurrentMediaView
          media={
            filteredMedias && filteredMedias.length > 0 && filteredMedias[currentMediaIndex]
              ? filteredMedias[currentMediaIndex]
              : { id: '', title: 'Sem mídia', url: '', type: 'image', selected_screens: [], active: false }
          }
          deviceCode={deviceCode}
          brightness={localBrightness}
          filteredMedias={filteredMedias}
          currentMediaIndex={currentMediaIndex}
          onPreviousMedia={onPreviousMedia}
          onNextMedia={onNextMedia}
          deviceId={deviceId}
          activatedAccount={currentUser}
          screens={screens}
          selectedScreen={selectedScreen}
          showDateTime={showDateTime}
          showNewsTicker={showNewsTicker}
          setShowNewsTicker={setShowNewsTicker}
          setShowDateTime={setShowDateTime}
        />

        {showNewsTicker && (
          <div className="fixed left-0 right-0 bottom-0 z-50 bg-black/80 text-white h-[50px] flex items-center overflow-hidden animate-fade-in">
            <div className="h-full w-[200px] bg-white flex items-center justify-center flex-shrink-0">
              <img
                src="https://locaninja.com/logotipo-watch.png"
                alt="Watch Logo"
                className="h-10 w-auto"
              />
            </div>
            <div className="flex-1 overflow-hidden px-4">
              <div className="animate-marquee inline-block whitespace-nowrap text-sm font-medium" style={{ letterSpacing: '0.02em' }}>
                {noticias && noticias.length > 0 ? noticias[noticiaIndex] : 'Carregando informações...'}
              </div>
            </div>
          </div>
        )}

        {/* Modal de Promoção */}
        {(() => {
          const mediaId = filteredMedias && filteredMedias[currentMediaIndex] ? filteredMedias[currentMediaIndex].id : '';
          let allow = !!(showPromoModal && activePromo);
          if (allow && activePromo && activePromo['Modal visível'] === 'LINKED') {
            const raw: any = activePromo.selected_media_ids as any;
            let ids: string[] = [];
            if (Array.isArray(raw)) {
              ids = raw.filter((v: any) => typeof v === 'string');
            } else if (typeof raw === 'string') {
              const s = raw.trim();
              let parsed: string[] | null = null;
              if (s.startsWith('[') && s.endsWith(']')) {
                try { parsed = JSON.parse(s); } catch { }
              }
              if (!parsed && s.startsWith('{') && s.endsWith('}')) {
                const inner = s.slice(1, -1);
                parsed = inner.split(',').map(x => x.trim().replace(/^"|"$/g, '').replace(/^'|'$/g, ''));
              }
              if (!parsed) {
                parsed = s.split(',').map(x => x.trim()).filter(Boolean);
              }
              ids = (parsed || []).filter(v => typeof v === 'string');
            }
            allow = ids.length === 0 ? true : ids.includes(mediaId);
          }
          return allow && activePromo ? (
            <PromoDisplayModal
              promo={activePromo}
              onClose={closePromoModal}
              deviceCode={deviceCode}
              screenId={selectedScreen}
              currentMediaIndex={currentMediaIndex}
              filteredMedias={filteredMedias}
              bottomOffset={showNewsTicker ? 70 : 16}
            />
          ) : null;
        })()}


      </div>
    </MediaDisplayErrorBoundary>
  );
}

export default memo(MediaDisplay);
